import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';
import { createFitTest } from '../../api/fitTests';
import { getFuncionarios } from '../../api/funcionarios';
import { getEquipamentos } from '../../api/equipamentos';
import { getEmpresas } from '../../api/empresas';
import { getLocais } from '../../api/locais';
import { useAuth } from '../../contexts/AuthContext';
import { addOneYear, todayIso as todayStr } from '../../utils/date';
import { GRUPOS_TESTE } from '../../utils/fitTestOpcoes';

const TIPOS = [
  { key: 'respiratorio', label: 'Respiratório', icon: 'bi-lungs', color: '#198754', bg: '#f8fff9', border: '#b8e0c0' },
  { key: 'auditivo',     label: 'Auditivo',     icon: 'bi-ear',   color: '#4361d8', bg: '#f8f9ff', border: '#c5d0f0' },
];

export default function FitTestNovo() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [funcionarios, setFuncionarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [locais, setLocais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    id_funcionario: searchParams.get('funcionario') || '',
    id_empresa: '',
    id_local: '',
    data_agendada: '',
    data_validade: '',
    observacoes: '',
    setor: '',
    funcao: '',
  });

  const [tipos, setTipos] = useState({ respiratorio: false, auditivo: false });

  const [formResp, setFormResp] = useState({
    id_equipamento: '',
    grupo_teste: 'qualitativo',
    faz_parte_brigada: false,
    faz_parte_controle_poco: false,
  });
  const [formAud, setFormAud] = useState({
    id_equipamento: '',
    grupo_teste: 'objetivo',
  });

  useEffect(() => {
    Promise.all([getFuncionarios(), getEmpresas(), getEquipamentos(), getLocais()]).then(([f, emp, eq, loc]) => {
      setFuncionarios(f); setEmpresas(emp); setEquipamentos(eq); setLocais(loc); setLoading(false);
    });
  }, []);

  // Auto-fill setor/funcao ao selecionar funcionário + empresa
  useEffect(() => {
    if (!form.id_funcionario || !form.id_empresa) return;
    const func = funcionarios.find(f => f.id_funcionario === form.id_funcionario);
    const vinculo = func?.vinculos?.find(v => v.id_empresa === form.id_empresa);
    if (vinculo) {
      setForm(prev => ({
        ...prev,
        setor: vinculo.ultimo_setor || '',
        funcao: vinculo.ultima_funcao || '',
      }));
    }
  }, [form.id_funcionario, form.id_empresa, funcionarios]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleAgendadaChange = (e) => {
    const val = e.target.value;
    setForm(prev => ({ ...prev, data_agendada: val, data_validade: addOneYear(val) }));
  };

  const toggleTipo = (key) => setTipos(t => ({ ...t, [key]: !t[key] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tipos.respiratorio && !tipos.auditivo) { setError('Selecione pelo menos um tipo de teste'); return; }
    if (!form.id_empresa) { setError('Selecione a empresa'); return; }
    if (tipos.respiratorio && !formResp.id_equipamento) { setError('Selecione o equipamento respiratório'); return; }
    if (tipos.auditivo && !formAud.id_equipamento) { setError('Selecione o equipamento auditivo'); return; }

    setError(''); setSaving(true);
    const comum = {
      id_funcionario: form.id_funcionario,
      id_empresa: form.id_empresa,
      id_local: form.id_local || null,
      data_agendada: form.data_agendada || null,
      data_validade: form.data_validade || null,
      observacoes: form.observacoes || null,
      setor: form.setor || null,
      funcao: form.funcao || null,
    };
    try {
      const tasks = [];
      if (tipos.respiratorio) {
        tasks.push(createFitTest({
          ...comum,
          tipo_teste: 'respiratorio',
          grupo_teste: formResp.grupo_teste,
          id_equipamento: formResp.id_equipamento,
          faz_parte_brigada: formResp.faz_parte_brigada,
          faz_parte_controle_poco: formResp.faz_parte_controle_poco,
        }));
      }
      if (tipos.auditivo) {
        const eq = equipamentos.find(e => e.id_equipamento === formAud.id_equipamento);
        tasks.push(createFitTest({
          ...comum,
          tipo_teste: 'auditivo',
          grupo_teste: formAud.grupo_teste,
          id_equipamento: formAud.id_equipamento,
          tipo_protetor_auricular: eq?.tipo_protetor_auricular || null,
        }));
      }
      await Promise.all(tasks);
      navigate('/fit-tests');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao registrar teste(s)');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const eqRespOpts = equipamentos
    .filter(e => e.tipo_teste === 'respiratorio')
    .map(e => ({ value: e.id_equipamento, label: `${e.ca} — ${e.tipo_mascara}${e.fabricante ? ` (${e.fabricante})` : ''}`, icon: 'bi-lungs', iconColor: '#198754' }));

  const eqAudOpts = equipamentos
    .filter(e => e.tipo_teste === 'auditivo')
    .map(e => ({ value: e.id_equipamento, label: `${e.ca} — ${e.tipo_mascara}${e.fabricante ? ` (${e.fabricante})` : ''}`, icon: 'bi-ear', iconColor: '#4361d8' }));

  return (
    <>
      <button className="btn btn-sm btn-link text-muted mb-3 ps-0" onClick={() => navigate('/fit-tests')}>
        <i className="bi bi-arrow-left me-1"></i>Voltar para Fit Tests
      </button>
      <PageHeader title="Novo Fit Test" subtitle="Registrar teste respiratório e/ou auditivo" />

      <div className="card-datafit p-4" style={{ maxWidth: 740 }}>
        {error && <div className="alert alert-danger py-2 small mb-3"><i className="bi bi-exclamation-triangle me-2"></i>{error}</div>}

        <form onSubmit={handleSubmit}>

          {/* ── Seleção de tipo(s) ── */}
          <div className="mb-4">
            <label className="form-label small fw-500 d-block mb-2">Tipo(s) de teste *</label>
            <div className="d-flex gap-3">
              {TIPOS.map(({ key, label, icon, color, bg }) => (
                <div key={key}
                  className="border rounded p-3 flex-fill text-center"
                  style={{ cursor: 'pointer', background: tipos[key] ? bg : '#fff', borderColor: tipos[key] ? color : '#dee2e6', borderWidth: tipos[key] ? 2 : 1, transition: 'all .15s', userSelect: 'none' }}
                  onClick={() => toggleTipo(key)}>
                  <i className={`bi ${icon} fs-3 d-block mb-1`} style={{ color: tipos[key] ? color : '#adb5bd' }} />
                  <div className="small fw-500" style={{ color: tipos[key] ? color : '#6c757d' }}>{label}</div>
                  {tipos[key] && <i className="bi bi-check-circle-fill d-block mt-1" style={{ color, fontSize: '.8rem' }} />}
                </div>
              ))}
            </div>
          </div>

          {/* ── Painel Respiratório ── */}
          {tipos.respiratorio && (
            <div className="border rounded p-3 mb-3" style={{ background: '#f8fff9', borderColor: '#b8e0c0' }}>
              <div className="small fw-bold text-uppercase mb-3" style={{ color: '#198754', letterSpacing: '.06em', fontSize: '.7rem' }}>
                <i className="bi bi-lungs me-1"></i>Respiratório
              </div>
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label small fw-500">Equipamento *</label>
                  <SearchableSelect options={eqRespOpts} value={formResp.id_equipamento}
                    onChange={id => setFormResp(f => ({ ...f, id_equipamento: id }))}
                    placeholder="Selecione o equipamento respiratório..." required={tipos.respiratorio} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-500">Grupo *</label>
                  <select className="form-select" value={formResp.grupo_teste}
                    onChange={e => setFormResp(f => ({ ...f, grupo_teste: e.target.value }))} required={tipos.respiratorio}>
                    {GRUPOS_TESTE.respiratorio.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div className="col-12 d-flex gap-4">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="brigada"
                      checked={formResp.faz_parte_brigada}
                      onChange={e => setFormResp(f => ({ ...f, faz_parte_brigada: e.target.checked }))} />
                    <label className="form-check-label small" htmlFor="brigada">Faz parte da brigada de incêndio</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="poco"
                      checked={formResp.faz_parte_controle_poco}
                      onChange={e => setFormResp(f => ({ ...f, faz_parte_controle_poco: e.target.checked }))} />
                    <label className="form-check-label small" htmlFor="poco">Faz parte do controle de poço</label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Painel Auditivo ── */}
          {tipos.auditivo && (
            <div className="border rounded p-3 mb-3" style={{ background: '#f8f9ff', borderColor: '#c5d0f0' }}>
              <div className="small fw-bold text-uppercase mb-3" style={{ color: '#4361d8', letterSpacing: '.06em', fontSize: '.7rem' }}>
                <i className="bi bi-ear me-1"></i>Auditivo
              </div>
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label small fw-500">Equipamento *</label>
                  <SearchableSelect options={eqAudOpts} value={formAud.id_equipamento}
                    onChange={id => setFormAud(f => ({ ...f, id_equipamento: id }))}
                    placeholder="Selecione o equipamento auditivo..." required={tipos.auditivo} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-500">Grupo *</label>
                  <select className="form-select" value={formAud.grupo_teste}
                    onChange={e => setFormAud(f => ({ ...f, grupo_teste: e.target.value }))} required={tipos.auditivo}>
                    {GRUPOS_TESTE.auditivo.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ── Campos comuns ── */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-500">Funcionário *</label>
              <select className="form-select" value={form.id_funcionario} onChange={set('id_funcionario')} required>
                <option value="">Selecione...</option>
                {funcionarios.map(f => <option key={f.id_funcionario} value={f.id_funcionario}>{f.nome}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-500">Empresa *</label>
              <select className="form-select" value={form.id_empresa} onChange={set('id_empresa')} required>
                <option value="">Selecione...</option>
                {empresas.map(emp => <option key={emp.id_empresa} value={emp.id_empresa}>{emp.nome_fantasia || emp.razao_social}</option>)}
              </select>
            </div>
          </div>

          {/* Setor e Função — auto-preenchidos do histórico */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-500">Setor</label>
              <input type="text" className="form-control" placeholder="Setor do funcionário"
                value={form.setor} onChange={set('setor')} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-500">Função</label>
              <input type="text" className="form-control" placeholder="Função do funcionário"
                value={form.funcao} onChange={set('funcao')} />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label small fw-500">Local de realização</label>
            <select className="form-select" value={form.id_local} onChange={set('id_local')}>
              <option value="">Sem local definido (opcional)</option>
              {locais.filter(l => !l.id_empresa || l.id_empresa === form.id_empresa)
                .map(l => <option key={l.id_local} value={l.id_local}>{l.nome}{l.tipo ? ` — ${l.tipo}` : ''}</option>)}
            </select>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-500">Data agendada</label>
              <input type="date" className="form-control" value={form.data_agendada} onChange={handleAgendadaChange}
                min={isAdmin ? undefined : todayStr()} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-500">Data de validade</label>
              <input type="date" className="form-control" value={form.data_validade} readOnly
                style={{ background: '#f0f2f5', color: '#6c757d', cursor: 'default' }} />
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label small fw-500">Observações</label>
            <textarea className="form-control" rows={3} value={form.observacoes} onChange={set('observacoes')} />
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn-datafit"
              disabled={saving || (!tipos.respiratorio && !tipos.auditivo)}>
              {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-check-lg me-2"></i>}
              {tipos.respiratorio && tipos.auditivo ? 'Salvar 2 testes' : 'Salvar teste'}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/fit-tests')}>Cancelar</button>
          </div>

        </form>
      </div>
    </>
  );
}
